import type { Instrument, SoundFamily } from "../data/instruments";
import { getDefaultTuningId, getDefaultVariantId, instruments } from "../data/instruments";

export const DEFAULT_AUDIO_LAB_SAMPLE_RATE = 44100;
export const DEFAULT_AUDIO_LAB_CHANNELS = 1;

export type AudioLabFamily = "string" | "wind" | "percussion" | "lamellophone" | "other";

export interface AudioLabPresetSnapshot {
  instrument: Instrument;
  defaultVariantId: string;
  defaultTuningId: string;
  sampleLayers: Array<{
    variantId: string;
    variantName: string;
    layers: Instrument["variants"][number]["sampleLayers"];
  }>;
}

export interface AudioLabInstrument {
  id: string;
  name: string;
  family: AudioLabFamily;
  region?: string;
  notes: string[];
  articulations: string[];
  defaultDurationSec: number;
  preset: AudioLabPresetSnapshot;
}

export interface AudioLabTestCase {
  instrumentId: string;
  instrumentName: string;
  note: string;
  articulation: string;
  durationSec: number;
  sampleRate: number;
  channels: number;
  filenameStem: string;
}

const soundFamilyToAudioLabFamily: Record<SoundFamily, AudioLabFamily> = {
  plucked_string: "string",
  plucked_harp_lute: "string",
  bowed_string: "string",
  hammered_string: "string",
  breathy_transverse_flute: "wind",
  breathy_end_blown_flute: "wind",
  reed: "wind",
  free_reed: "wind",
  membrane_drum: "percussion",
  metal_idiophone: "other",
  bronze_idiophone: "other",
  wood_idiophone: "other",
  lamellophone: "lamellophone",
  drone_aerophone: "wind"
};

const fallbackArticulationsByFamily: Record<AudioLabFamily, string[]> = {
  string: ["normal", "pluck", "vibrato", "bend", "muted", "tremolo"],
  wind: ["normal", "breath", "vibrato", "micro_bend", "accent"],
  percussion: ["normal", "open", "muted", "rim", "pressure"],
  lamellophone: ["normal", "pluck", "buzz", "muted", "tremolo"],
  other: ["normal", "strike", "damped", "accent", "tremolo"]
};

const instrumentSpecificFallbackArticulations: Record<string, string[]> = {
  gayageum: ["nonghyeon", "jeonseong", "toeseong"],
  erhu: ["rou_xian", "portamento"],
  oud: ["micro_bend", "tremolo"],
  sitar: ["meend", "chikari"],
  daegeum: ["yoseong", "toeseong"],
  ney: ["micro_bend", "breath"],
  janggu: ["gung", "chaepyeon", "both_open"],
  tabla: ["bayan_pressure", "dayan", "muted"],
  mbira: ["buzz", "muted"],
  kora: ["thumb_index_interlock", "left_thumb", "right_index"],
  yidaki_didgeridoo: ["drone", "mouth_shape_xy", "vocalization"],
  gamelan_saron: ["keyup_damping", "ombak"]
};

function unique(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)));
}

function getTechniqueIds(instrument: Instrument): string[] {
  return instrument.articulations.flatMap((profile) => profile.techniques.map((technique) => technique.id));
}

function getNoteLabels(instrument: Instrument): string[] {
  const tuningNotes = instrument.tunings.flatMap((tuning) => tuning.notes.map((note) => note.label));

  return unique([...instrument.playableNotes, ...tuningNotes]).slice(0, 8);
}

function getDurationSec(family: AudioLabFamily, instrumentId: string): number {
  if (family === "percussion") {
    return 1.35;
  }

  if (instrumentId === "yidaki_didgeridoo") {
    return 3.2;
  }

  if (family === "wind" || family === "string") {
    return 2.8;
  }

  return 2.2;
}

export function sanitizeFilenamePart(value: string): string {
  return value
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
}

export function getTestFilenameStem(instrumentId: string, note: string, articulation: string): string {
  return [instrumentId, note, articulation].map(sanitizeFilenamePart).join("_");
}

export function getAudioLabInstruments(): AudioLabInstrument[] {
  return instruments.map((instrument) => {
    const family = soundFamilyToAudioLabFamily[instrument.classification.family] ?? "other";
    const techniqueIds = getTechniqueIds(instrument);
    const fallbackArticulations = fallbackArticulationsByFamily[family];
    const instrumentFallbackArticulations = instrumentSpecificFallbackArticulations[instrument.id] ?? [];
    const articulations = unique(["normal", ...techniqueIds, ...instrumentFallbackArticulations, ...fallbackArticulations]);
    const defaultVariantId = getDefaultVariantId(instrument.id);
    const defaultTuningId = getDefaultTuningId(instrument.id);

    return {
      id: instrument.id,
      name: instrument.name,
      family,
      region: instrument.origin,
      notes: getNoteLabels(instrument),
      articulations,
      defaultDurationSec: getDurationSec(family, instrument.id),
      preset: {
        instrument,
        defaultVariantId,
        defaultTuningId,
        sampleLayers: instrument.variants.map((variant) => ({
          variantId: variant.id,
          variantName: variant.name,
          layers: variant.sampleLayers
        }))
      }
    };
  });
}

export function getAudioLabInstrumentById(instrumentId: string): AudioLabInstrument {
  return getAudioLabInstruments().find((instrument) => instrument.id === instrumentId) ?? getAudioLabInstruments()[0];
}

export function getAudioLabTestCases(instrumentId: string): AudioLabTestCase[] {
  const instrument = getAudioLabInstrumentById(instrumentId);

  return instrument.notes.flatMap((note) =>
    instrument.articulations.map((articulation) => ({
      instrumentId: instrument.id,
      instrumentName: instrument.name,
      note,
      articulation,
      durationSec: instrument.defaultDurationSec,
      sampleRate: DEFAULT_AUDIO_LAB_SAMPLE_RATE,
      channels: DEFAULT_AUDIO_LAB_CHANNELS,
      filenameStem: getTestFilenameStem(instrument.id, note, articulation)
    }))
  );
}

export function getAllAudioLabTestCases(): AudioLabTestCase[] {
  return getAudioLabInstruments().flatMap((instrument) => getAudioLabTestCases(instrument.id));
}

export function getTotalAudioLabTestCount(): number {
  return getAllAudioLabTestCases().length;
}
